import { getActiveAppCode } from "@/config/config";
import {
	AuthLoginVendorDocument,
	type AuthLoginVendorMutation,
	AuthRefreshTokenDocument,
	type AuthRefreshTokenMutation,
} from "@/generated/graphql";
import { useSyncExternalStore } from "react";
import { urqlClient } from "../graphql/client";
import { SKIP_ERROR_TOAST_CONTEXT } from "../graphql/graphqlErrorToast";

export type K3Credentials = { username: string; password: string };

export type K3User = { vendorID?: string } & Record<string, unknown>;

export type K3Session = {
	user: K3User;
	accessToken: string;
	refreshToken?: string | null;
	permissions: string[];
};

export type AuthStatus = "authenticated" | "unauthenticated";

export type AuthState = {
	status: AuthStatus;
	session: K3Session | null;
	user: K3User | null;
};

// Same key keeps old sessions.
const SESSION_STORAGE_KEY = "k3-auth";
const EXPIRED_TOKEN_MARKERS = ["token is expired", 'exp" not satisfied'];
const ACCOUNT_STORAGE_PREFIX = "portal-";

type AppRolePermission = {
	appID?: string | null;
	roles?: { permissions?: { permissionID?: string | null }[] | null }[] | null;
};

const collectPermissionIDs = (
	appRolePermissions: AppRolePermission[] | null | undefined,
) =>
	(appRolePermissions ?? [])
		.filter(
			(appRolePermission) => appRolePermission.appID === getActiveAppCode(),
		)
		.flatMap((appRolePermission) =>
			(appRolePermission.roles ?? []).flatMap((role) =>
				(role.permissions ?? []).map((permission) => permission.permissionID),
			),
		)
		.filter(
			(permissionID): permissionID is string =>
				typeof permissionID === "string",
		);

const readStoredSession = (): K3Session | null => {
	try {
		const storedSession = JSON.parse(
			localStorage.getItem(SESSION_STORAGE_KEY) ?? "null",
		) as K3Session | null;
		return storedSession?.accessToken ? storedSession : null;
	} catch {
		return null;
	}
};

const toState = (session: K3Session | null): AuthState => ({
	status: session ? "authenticated" : "unauthenticated",
	session,
	user: session?.user ?? null,
});

let currentState = toState(readStoredSession());
const stateListeners = new Set<() => void>();

const setSession = (session: K3Session | null) => {
	try {
		if (session)
			localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
		else localStorage.removeItem(SESSION_STORAGE_KEY);
	} catch {
		// Session lasts this tab.
	}
	currentState = toState(session);
	for (const listener of stateListeners) listener();
};

const clearAccountStorage = () => {
	for (const storage of [localStorage, sessionStorage]) {
		try {
			const accountKeys = Object.keys(storage).filter((storageKey) =>
				storageKey.startsWith(ACCOUNT_STORAGE_PREFIX),
			);
			for (const accountKey of accountKeys) storage.removeItem(accountKey);
		} catch {
			// Storage unavailable.
		}
	}
};

const isExpiredTokenMessage = (message: string) =>
	EXPIRED_TOKEN_MARKERS.some((marker) => message.includes(marker));

export const authAdapter = {
	getState: () => currentState,
	getToken: () => currentState.session?.accessToken ?? null,
	subscribe: (listener: () => void) => {
		stateListeners.add(listener);
		return () => {
			stateListeners.delete(listener);
		};
	},
	login: async (credentials: K3Credentials) => {
		const result = await urqlClient
			.mutation<AuthLoginVendorMutation>(
				AuthLoginVendorDocument,
				{ input: credentials },
				SKIP_ERROR_TOAST_CONTEXT,
			)
			.toPromise();
		if (result.error) throw result.error;
		const payload = result.data?.authLoginVendor;
		if (!payload?.accessToken || !payload.user) {
			throw new Error("Invalid login response");
		}
		setSession({
			user: payload.user as K3User,
			accessToken: payload.accessToken,
			refreshToken: payload.refreshToken,
			permissions: collectPermissionIDs(payload.appRolePermissions),
		});
	},
	refresh: async () => {
		const session = currentState.session;
		if (!session?.refreshToken) return;
		const result = await urqlClient
			.mutation<AuthRefreshTokenMutation>(
				AuthRefreshTokenDocument,
				{ refreshToken: session.refreshToken },
				{ skipAuth: true },
			)
			.toPromise();
		if (result.error) {
			const errorMessage =
				result.error.graphQLErrors?.[0]?.message ?? result.error.message;
			if (isExpiredTokenMessage(errorMessage)) setSession(null);
			throw result.error;
		}
		const payload = result.data?.authRefreshToken;
		if (!payload?.accessToken) throw new Error("Invalid refresh response");
		setSession({
			...session,
			user: (payload.user as K3User | null) ?? session.user,
			accessToken: payload.accessToken,
			refreshToken: payload.refreshToken ?? session.refreshToken,
			permissions: collectPermissionIDs(payload.appRolePermissions),
		});
	},
	logout: async () => {
		clearAccountStorage();
		setSession(null);
	},
	isExpiredTokenMessage,
};

export type AuthAdapter = typeof authAdapter;

export function useAuthSession() {
	return useSyncExternalStore(authAdapter.subscribe, authAdapter.getState);
}
